import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Marquee } from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";
import { IMAGES, VIDEOS } from "@/lib/images";

const EASE = [0.16, 1, 0.3, 1];

const HeroLine = ({ children, delay }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  </div>
);

const Hero = () => {
  const { scrollY } = useScroll();
  const yImage = useTransform(scrollY, [0, 700], [0, -60]);
  const yGlow = useTransform(scrollY, [0, 700], [0, 40]);

  return (
    <section data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute -right-32 top-1/4 h-[34rem] w-[34rem] rounded-full bg-gold/[0.08] blur-3xl"
      />
      <div className="mx-auto grid w-full max-w-7xl gap-16 px-6 pb-24 pt-36 lg:grid-cols-[1.2fr_1fr] lg:items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-gold"
            data-testid="hero-eyebrow"
          >
            Singer • Inspirational Vocalist • Intimate Live Music
          </motion.p>

          <h1 className="mt-6 font-serif font-light leading-[0.95] tracking-tight text-sand-bright">
            <HeroLine delay={0.15}>
              <span className="block text-6xl sm:text-8xl lg:text-9xl">Derrick</span>
            </HeroLine>
            <HeroLine delay={0.28}>
              <span className="block text-6xl sm:text-8xl lg:text-9xl italic text-gold-light">Hall</span>
            </HeroLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
            className="mt-6 font-serif text-2xl sm:text-3xl font-light italic text-sand"
            data-testid="hero-tagline"
          >
            Bringing the Concert to You
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.62, ease: EASE }}
            className="mt-5 max-w-xl text-base sm:text-lg font-light leading-relaxed text-mist"
            data-testid="hero-intro"
          >
            Live music for homes, churches, senior living communities, Christmas
            programs, special events, and meaningful gatherings.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/music"
              data-testid="hero-cta-listen"
              className="bg-gold px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal-950 transition-colors duration-300 hover:bg-gold-light"
            >
              Listen
            </Link>
            <Link
              to="/videos"
              data-testid="hero-cta-watch"
              className="border border-white/20 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-sand transition-colors duration-300 hover:border-gold/60 hover:text-gold-light"
            >
              Watch
            </Link>
            <Link
              to="/booking"
              data-testid="hero-cta-book"
              className="group flex items-center gap-2 px-2 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"
            >
              Bring the Concert to You
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
          className="relative hidden lg:block"
        >
          <div className="absolute -left-4 -top-4 h-full w-full border border-gold/30" />
          <motion.div style={{ y: yImage }} className="relative overflow-hidden">
            <img
              src={IMAGES.micHero}
              alt="Vintage microphone illuminated by warm stage light"
              className="aspect-[4/5] w-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal-950/70 via-transparent to-transparent" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const ConcertToYou = () => (
  <section data-testid="section-concert-to-you" className="border-b border-white/[0.06]">
    <div className="mx-auto max-w-3xl px-6 py-24 text-center sm:py-32">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">An Invitation</p>
        <h2 className="mt-4 font-serif text-3xl sm:text-5xl font-light tracking-tight text-sand-bright">
          The Concert Comes to You
        </h2>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="mt-10 text-base sm:text-lg font-light leading-relaxed text-mist" data-testid="concert-to-you-text">
          Not every meaningful musical experience needs a large stage. Derrick Hall
          brings Christian music into the places where people already gather — your
          living room, backyard, church, community room, or special family gathering.
        </p>
        <p className="mt-6 text-base sm:text-lg font-light leading-relaxed text-mist">
          Whether it is an intimate evening with friends, a time of encouragement for a
          family, or a special program for a senior living community, the goal is
          simple: create a warm, personal moment through music, faith, and connection.
        </p>
      </Reveal>
      <Reveal delay={0.2}>
        <div className="mx-auto mt-10 h-px w-16 bg-gold/50" />
      </Reveal>
    </div>
  </section>
);

const SectionShell = ({ testId, eyebrow, title, children, className = "" }) => (
  <section data-testid={testId} className={`mx-auto max-w-7xl px-6 py-24 sm:py-32 ${className}`}>
    <Reveal>
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">{eyebrow}</p>
      <h2 className="mt-4 max-w-2xl font-serif text-3xl sm:text-5xl font-light tracking-tight text-sand-bright">
        {title}
      </h2>
    </Reveal>
    {children}
  </section>
);

const MeetDerrick = () => (
  <SectionShell testId="section-meet-derrick" eyebrow="The Artist" title="Meet Derrick">
    <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:items-center">
      <Reveal delay={0.1}>
        <div className="relative">
          <div className="absolute -bottom-4 -right-4 h-full w-full border border-gold/30" />
          <img
            src={IMAGES.micStand}
            alt="Vocal microphone on a stand, ready for performance"
            className="relative aspect-[4/3] w-full object-cover"
            data-testid="meet-derrick-image"
          />
        </div>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="text-base sm:text-lg font-light leading-relaxed text-mist" data-testid="meet-derrick-text">
          Derrick Hall is a singer and inspirational vocalist who brings warm, intimate
          live music into the places people already gather — homes, churches, senior
          living communities, special events, and meaningful occasions.
        </p>
        <p className="mt-6 text-base sm:text-lg font-light leading-relaxed text-mist">
          This site is the home of his musical life — recordings, live performances,
          videos, and opportunities to bring the concert to you. His repertoire includes
          Christian and inspirational music, Christmas programs, patriotic and
          remembrance selections, and music for a variety of special gatherings.
        </p>
        <p className="mt-6 text-base sm:text-lg font-light leading-relaxed text-mist">
          His full story is on its way. For now, consider this an open door.
        </p>
        <Link
          to="/about"
          data-testid="meet-derrick-link"
          className="group mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"
        >
          More about Derrick
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </SectionShell>
);

const COVERS = [
  { id: "father-me", title: "Father Me", src: "/images/covers/father-me.jpg" },
  { id: "feel-the-nail", title: "Feel The Nail", src: "/images/covers/feel-the-nail.jpg" },
  {
    id: "lord-i-lift-your-name-on-high",
    title: "Lord I Lift Your Name On High",
    src: "/images/covers/lord-i-lift-your-name-on-high.jpg",
  },
];

const MusicPreview = () => (
  <SectionShell testId="section-music-preview" eyebrow="Listen" title="Music" className="border-t border-white/[0.06]">
    <Reveal delay={0.1}>
      <div className="mt-14 border border-white/[0.08] bg-charcoal-900/60 p-8 sm:p-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Selected Recordings</p>
        <p className="mt-4 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-mist" data-testid="music-preview-text">
          Listen to Christian and inspirational recordings by Derrick Hall. Hear samples
          from Father Me, Feel The Nail, and Lord I Lift Your Name On High, then visit
          the Music page to explore the recordings.
        </p>
        <div className="mt-10 grid max-w-2xl grid-cols-3 gap-4 sm:gap-6" data-testid="music-preview-covers">
          {COVERS.map((c) => (
            <figure key={c.id}>
              <img
                src={c.src}
                alt={`${c.title} — cover artwork`}
                data-testid={`music-preview-cover-${c.id}`}
                className="aspect-square w-full border border-gold/20 object-cover"
              />
              <figcaption className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.15em] leading-relaxed text-mist">
                {c.title}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </Reveal>
    <Reveal delay={0.2}>
      <Link
        to="/music"
        data-testid="music-preview-link"
        className="group mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"
      >
        Visit the music page
        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </Reveal>
  </SectionShell>
);

const WatchPreview = () => (
  <SectionShell testId="section-watch-preview" eyebrow="Watch" title="Watch Derrick Perform" className="border-t border-white/[0.06]">
    <Reveal delay={0.1}>
      <div className="relative mt-14 overflow-hidden border border-gold/25" data-testid="watch-preview-frame">
        <video
          src={VIDEOS.teachMeToLove}
          poster="/images/teach-me-to-love-poster.jpg"
          controls
          preload="metadata"
          className="aspect-video w-full bg-charcoal-900 object-cover"
          data-testid="home-featured-video"
        />
      </div>
      <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-gold" data-testid="home-featured-video-title">
        Teach Me to Love — Performance Video
      </p>
    </Reveal>
    <Reveal delay={0.2}>
      <Link
        to="/videos"
        data-testid="watch-preview-link"
        className="group mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"
      >
        Visit the video gallery
        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </Reveal>
  </SectionShell>
);

const PerformancesIntro = () => (
  <SectionShell testId="section-performances-intro" eyebrow="Where Derrick Sings" title="Live Performances" className="border-t border-white/[0.06]">
    <Reveal delay={0.1}>
      <p className="mt-8 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-mist" data-testid="performances-intro-text">
        Live music, close to home. Derrick brings Christian, inspirational, patriotic,
        seasonal, and familiar songs to at-home concerts, churches and faith gatherings,
        senior living communities, weddings, anniversaries, Christmas concerts,
        remembrance programs, and social gatherings — intimate settings where music can
        truly connect.
      </p>
    </Reveal>
    <Reveal delay={0.2}>
      <Link
        to="/performances"
        data-testid="performances-intro-link"
        className="group mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"
      >
        Explore the settings
        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </Reveal>
  </SectionShell>
);

const BookingBanner = () => (
  <section data-testid="section-booking-banner" className="relative overflow-hidden border-t border-white/[0.06]">
    <img
      src={IMAGES.spotlight}
      alt=""
      aria-hidden="true"
      className="absolute inset-0 h-full w-full object-cover opacity-25"
    />
    <div className="absolute inset-0 bg-gradient-to-r from-charcoal-950 via-charcoal-950/85 to-charcoal-950/40" />
    <div className="relative mx-auto max-w-7xl px-6 py-28 sm:py-36">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Booking Inquiry</p>
        <h2 className="mt-4 max-w-3xl font-serif text-3xl sm:text-5xl font-light leading-tight tracking-tight text-sand-bright">
          Bring the concert to you.
        </h2>
        <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-mist">
          Planning an evening at home, a church program, or music for a senior living
          community? Share a few details about your gathering and Derrick will respond
          personally. No rates are published — every inquiry receives individual attention.
        </p>
        <Link
          to="/booking"
          data-testid="booking-banner-cta"
          className="mt-10 inline-block bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal-950 transition-colors duration-300 hover:bg-gold-light"
        >
          Invite Derrick to Your Gathering
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Derrick Hall — Christian Singer | Bringing the Concert to You"
        siteName="Derrick Hall"
        description="Derrick Hall is a Christian singer bringing warm, intimate music to homes, churches, senior living communities, and meaningful gatherings."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Derrick Hall",
          jobTitle: "Christian Singer / Vocalist",
        }}
      />
      <Hero />
      <Marquee />
      <ConcertToYou />
      <MeetDerrick />
      <MusicPreview />
      <WatchPreview />
      <PerformancesIntro />
      <BookingBanner />
    </>
  );
}
